import type { CISProviderBehavior, PaymentMethodToggles } from '../types';
import type { CartItem } from '../../types/CartItem';
import type { Item } from '../../typegen/item';
import { PaymentType } from '../../typegen/payment-type';

const isBlocked = (item: Item, paymentType: PaymentType) =>
	item.paymentMethods?.some((method) => method.paymentType === paymentType && !method.isAllowed) ?? false;

const blockedItemNumbers = (items: Item[], paymentType: PaymentType) =>
	items.filter((item) => isBlocked(item, paymentType)).map((item) => item.itemNumber);

export const invoiceCloudCISBehavior: CISProviderBehavior = {
	label: 'InvoiceCloud',
	getTestPanelConfig() {
		return { show: true, altText: 'InvoiceCloud' };
	},

	// An IC address or phone search can return several accounts, each with its own
	// BlockPaymentsCC / BlockPaymentsACH. A method is disabled for the session only when every
	// returned account blocks it; otherwise it is enforced per cart item in applyCartPaymentMethodBlocks.
	getLookupPaymentMethodToggles(items, log) {
		const blockedForAll = (paymentType: PaymentType) => items.length > 0 && items.every((item) => isBlocked(item, paymentType));
		const toggles: PaymentMethodToggles = {
			cash: !blockedForAll(PaymentType.Cash),
			card: !blockedForAll(PaymentType.Card),
			check: !blockedForAll(PaymentType.Check),
		};

		const blocks = {
			cash: blockedItemNumbers(items, PaymentType.Cash),
			card: blockedItemNumbers(items, PaymentType.Card),
			check: blockedItemNumbers(items, PaymentType.Check),
		};
		if (blocks.cash.length > 0 || blocks.card.length > 0 || blocks.check.length > 0) {
			log('Invoice Cloud lookup returned account-level payment method blocks', {
				itemCount: items.length,
				blockedBy: blocks,
				enabledForSession: toggles,
			});
		}

		return toggles;
	},

	// Checked against the cart directly because IC kiosks' payment buttons have no PaymentTypes
	// configured, so kioskStore.canPayAmountWithPaymentMethod never runs its per-item check.
	applyCartPaymentMethodBlocks(cartItems: CartItem[], enabled, log) {
		const items = cartItems.map((cartItem) => cartItem.item);
		const blocks = {
			cash: blockedItemNumbers(items, PaymentType.Cash),
			card: blockedItemNumbers(items, PaymentType.Card),
			check: blockedItemNumbers(items, PaymentType.Check),
		};
		const result: PaymentMethodToggles = {
			cash: enabled.cash && blocks.cash.length === 0,
			card: enabled.card && blocks.card.length === 0,
			check: enabled.check && blocks.check.length === 0,
		};

		log('Invoice Cloud payment method button states', {
			cartItems: items.map((item) => item.itemNumber),
			enabledBeforeCartCheck: enabled,
			blockedByCartItems: blocks,
			enabled: result,
		});

		return result;
	},
};
